import { initializeWithConfig, track, updateUser, updateSubscriptions } from "@iterable/web-sdk";
import { v4 as uuidv4 } from "uuid";
import { Core } from './core'

export class Iterable {
  constructor () {
    if (Core.isAdmin()) return
    if ( !window?.cgd?.ite_is_active ) return;

    this.selector = '[data-ite]';
    this.siteUrl = 'bim.cegid.com';
    this.cloudUrl = 'bim.cegid.cloud';
    this.jwtTokenUrl = '/wp-json/cegid/v1/generate-token';
    this.uuid = '';
    this.isNewUser = false;
    this.iterableData = this.getIterableData() || {};

    this.setUuid();
    this.passTrackingParamsToExternalCloudLinks();
    this.initIterable();
  }

  passDataToZendesk() {

    // Only desktop
    if ( Core.isBpLessThan('xl') ) {
      return;
    }

    const instance = this;

    function setMessenger() {
      if ( window?.zE ) {
        window.zE(
          'messenger:set',
          'conversationFields',
          [
            {
              id: instance.getMessengerId(),
              value: instance.uuid
            },
          ]
        );
      }
    }

    window?.zE ? setMessenger() : window.addEventListener( 'load', setMessenger ); // eslint-disable-line
  }

  getMessengerId() {
    return window?.cgd?.zeMessengerId;
  }

  setUuid() {
    let uuid = '';

    // 1. Check if user has cookie "tid" then set uuid based on this value
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };
    uuid = getCookie('tid');

    // 2. Check if there is a "tid" param in the url, set uuid based on this value
    if (!uuid) {
      const urlParams = new URLSearchParams(window.location.search);
      const tidParam = urlParams.get('tid');
      if (tidParam) {
        uuid = tidParam;
      }
    }

    // 3. fallback, generate uuid using uuidv4() function
    if (!uuid) {
      uuid = uuidv4();
      this.isNewUser = true;
    }

    // Bonus step: store uuid value in a cookie "tid" for 1 year
    const setCookie = (name, value, days = 365) => {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/`;
    };
    setCookie('tid', uuid);

    this.uuid = uuid;
  }

  addUUIDtoExternalCloudLinks() {
    document.querySelectorAll('a[href*="' + this.cloudUrl + '"]').forEach(link => {
      try {
        const url = new URL(link.href);
        if (url.hostname === this.cloudUrl) {
          url.searchParams.set('tid', this.uuid);
          link.href = url.toString();
        }
      } catch (e) {
        //console.warn('Lien ignoré car mal formé :', link.href);
      }
    });
  }

  /**
   * Pass tracking params from btrack cookie and UUID to external cloud links.
   * Adds tid (UUID) and all params from btrack cookie (utm_*, gclid, fbclid, etc)
   * to the URL of links pointing to cloudUrl.
   */
  passTrackingParamsToExternalCloudLinks() {
    // Get btrack cookie data
    const btrackCookie = this.getCookie('btrack')
    const btrackData = btrackCookie ? JSON.parse(btrackCookie) : {}

    document.querySelectorAll('a[href*="' + this.cloudUrl + '"]').forEach(link => {
      try {
        const url = new URL(link.href)
        if (url.hostname === this.cloudUrl) {
          // Always add tid (UUID)
          url.searchParams.set('tid', this.uuid)

          // Add all tracking params from btrack cookie
          Object.entries(btrackData).forEach(([key, value]) => {
            url.searchParams.set(key, value)
          })

          link.href = url.toString()
        }
      } catch {
        // Ignore malformed URLs
      }
    })
  }

  /**
   * Get a cookie value by name.
   *
   * @param {string} name Cookie name.
   * @returns {string|null} Cookie value or null if not found.
   */
  getCookie(name) {
    const nameEQ = name + '='
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim()
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length))
      }
    }
    return null
  }

  /**
   * Génère un token JWT pour Iterable via l'API REST WordPress
   *
   * @param {string|undefined} userId
   * @returns {Promise<string>} Le token JWT
   */
  async generateIterableToken(userId) {
      if (!userId) {
          console.error("The user hasn't any tracking ID.");
          throw new Error("Missing userId for JWT generation.");
      }

      try {
          const res = await fetch(`${this.jwtTokenUrl}?user_id=${encodeURIComponent(userId)}`);
          if (!res.ok) {
              const error = await res.json();
              console.error("Failed to fetch JWT token:", error);
              throw new Error(error?.error || "Unknown error");
          }

          const data = await res.json();
          if (!data.token) {
              throw new Error("Token not returned in response.");
          }

          return data.token;

      } catch (err) {
          console.error("Error generating JWT token:", err);
          throw err;
      }
  }

  async initIterable() {
    const { setUserID } = initializeWithConfig({
      authToken: window?.cgd?.iAp,
      configOptions: {
        isEuIterableService: true,
      },

      generateJWT: async ({ userID }) => {
        return await this.generateIterableToken( userID );
      }
    });

    await setUserID(this.uuid).then(() => {
      this.setEvents();
    });
  }

  setEvents() {
    // this.trackLinks()
    this.trackUTMOnUser()
    this.trackPageViews()
    this.trackACFEForms()
    this.passDataToZendesk()
  }

  trackLinks() {
    // document.querySelectorAll('a[href*="://"]').forEach(link => {
    //   link.addEventListener('click', (ev) => this.trackLink(ev)?.bind(this));
    // });
  }

  trackUTMOnUser() {
    const UTM = this.getUTMParamsFromUrl();
    if ( !Object.values(UTM).length ) {
      return;
    }

    updateUser({
      userId: this.uuid,
      dataFields: UTM
    }).then((response) => {
      if (response.status != 200) {
        console.log("Failure user update")
      }
    }).catch((error) => {
      console.log("Error user update: ", error.response.data);
    });
  }

  getUTMParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};

    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
      if (params.has(key)) {
        let iteKey = '';
        if ( key == 'utm_source' ) {
          iteKey = 'utm_Source';
        } else if ( key == 'utm_medium' ) {
          iteKey = 'utm_Medium';
        } else if ( key == 'utm_campaign' ) {
          iteKey = 'utm_CampaignId';
        }
        utmParams[iteKey] = params.get(key);
      }
    });

    return utmParams;
  }

  trackPageView({ eventName, url, pageName }) {

    if ( !eventName ) { return; }

    track({
      eventName,
      url,
      pageName,
      dataFields: {
        url,
      }
    }).then((response) => {
      if (response.status != 200) {
        console.log("Failure tracking page view")
      }
    }).catch((error) => {
      console.log("Error tracking page view: ", error.response.data);
    });
  }

  trackLPViewForNewUser({ url, pageName }) {

    if ( !pageName || !this.isNewUser ) { return; }

    if ( !document.body.classList.contains( 'single-landing-page' ) ) {
      return;
    }

    updateUser({
      userId: this.uuid,
      dataFields: {
        url,
        fromLP: true,
        thematicLP: pageName,
      }
    }).then((response) => {
      if (response.status != 200) {
        console.log("Failure LP tracking")
      }
    }).catch((error) => {
      console.log("Error LP tracking: ", error.response.data);
    });
  }

  getIterableData() {
    const el = document.querySelector(this.selector);
    let iteData = el?.dataset?.ite;
    if ( !iteData ) return;

    iteData = JSON.parse(iteData);

    const utmData = this.getUTMParamsFromUrl();
    const mergedData = {...iteData, ...utmData};

    return mergedData;
  }

  trackPageViews() {
    this.trackPageView(this.iterableData);
    this.trackLPViewForNewUser(this.iterableData);
  }

  trackLink(ev) {
    // ev.preventDefault();
    // TODO: needs infos about which data to pass (ex: messageId which causes an error at the moment)
    // trackInAppClick({
    //   userId: this.uuid,
    //   clickedUrl: ev.target.href,
    //   messageId: '0', // ?? which id should we use here?
    //   deviceInfo: {
    //     appPackageName: this.siteUrl,
    //     deviceId: JSON.stringify(navigator.userAgent),
    //   }
    // }).then((response) => {
    //   if (response.status != 200) {
    //     console.log("Failure tracking embedded click")
    //   }
    // }).catch((error) => {
    //   console.log("Error tracking embedded click: ", error.response.data);
    // });

  }

  trackACFEForms() {

    if ( !window?.acf ) { return; }
    if ( !window?.acfe ) { return; }

    const acfeForms = acfe.getForms();
    if ( !acfeForms.length ) { return; }

    // this.onACFEFormSuccess(acfeForm);
    window.acf.add_action('validation_success', this.onACFEFormSuccess.bind(this));
  }

  onACFEFormSuccess( $form ) {

    const formEl = $form?.jquery ? $form[0] : $form;

    // Stop submit
    const stopSubmit = (ev) => ev.preventDefault();
    formEl.addEventListener('submit', stopSubmit);

    const formData = new FormData(formEl);
    const formValues = []
    formData.entries().forEach((v) => {
      const acfKey = v[0].replace('acf[', '').replace(']','');
      if ( acfKey === '_validate_email' ) return;

      const acfField = acf.getField(acfKey);
      if ( !acfField.$el.length ) return;

      // Push acf value
      formValues[acfField?.data?.name] = acfField.val();
    });

    // Format Iterable data
    // const iterableEventData = {
    //   ...this.iterableData,
    //   // eventName: formValues?.ite_event_name,
    //   campaignId: Number(formValues?.ite_campaign_id),
    //   dataFields: {
    //     ...formValues,
    //   }
    // };

    // Push iterable data
    updateSubscriptions({
      subscribedMessageTypeIds: [Number(formValues?.ite_campaign_id)],
    }).then(() => {
        console.log('✔', 'Iterable User Suscribed!'); // eslint-disable-line

        // Restore submit & submit
        formEl.removeEventListener('submit', stopSubmit);
        // acf.unlockForm($form);
        formEl.submit();
      })
      .catch((err) => {
        console.error('❌ Iterable Subscription failed', err.response); // eslint-disable-line

        // Restore submit & unlock form & submit
        formEl.removeEventListener('submit', stopSubmit);
        // acf.unlockForm($form);
        formEl.submit();
      });

  }
}
